<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :min-w="3"
        :min-h="minHeight"
        :resized="handleResized"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title-prepend>
            <go-back-icon-button @click="goBack" v-if="isTableView" />
        </template>
        <template v-slot:card-title>
            {{ title }}
        </template>
        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <vue-apex-charts v-if="ready && !isTableView" height="390" :options="options" :series="series" />
            <departures-card-grid
                v-if="isTableView"
                :getDataParams="getDataParams"
                :getDataEndpoint="getDataEndpoint"
                :cardGridKey="cardGridKey"
                :limit="limit"
                :tabWidget="tabWidget"
            />
        </template>
        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
                :withBlockedReservations="true"
                :withTableFontSize="true"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import CardGridItem from '../common/card-grid-item.vue';
import CardGridItemBase from '../common/card-grid-item-base';
import DeparturesCardGrid from '../common/departures-card-grid.vue';
import { DASHBOARD_DEPARTURES_CHART_DATA_ENDPOINT, DASHBOARD_DEPARTURES_GRID_ENDPOINT } from '../../../../endpoints';
import GoBackIconButton from '../../../generic/go-back-icon-button/go-back-icon-button.vue';
import { getWidgetGridRowsNumber, getApexChartsGradientFill } from '../helpers';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import VueApexCharts from 'vue3-apexcharts';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

export default {
    name: 'DeparturesChartCardGridItem',
    mixins: [cardGridItemBaseMixin],

    components: {
        BusyModal,
        CardGridItem,
        TabWidgetSettingsModalForm,
        VueApexCharts,
        ResponsiveButton,
        GoBackIconButton,
        DeparturesCardGrid,
    },
    data() {
        return {
            autoWidthExclude: ['guest', 'property_name'],
            busy: null,
            chartData: [],
            isTableView: false,
            selectedDay: null,
            limit: getWidgetGridRowsNumber({
                tabWidget: this.tabWidget,
            }),
        };
    },
    computed: {
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                    display_blocked_reservations: this.tabWidget.settings.display_blocked_reservations,
                    table_font_size: this.tabWidget.settings.table_font_size || this.defaultWidgetFontSize,
                },
            };
        },
        series() {
            return [
                {
                    data: this.chartData
                        .map((departuresCount) => departuresCount.count)
                        .slice(0, this.tabWidget.w > 6 ? 14 : 7),
                },
            ];
        },
        options() {
            return {
                chart: {
                    type: 'area',
                    toolbar: {
                        show: false,
                    },
                    zoom: {
                        enabled: false,
                    },
                    events: {
                        markerClick: (event, chartContext, { seriesIndex, dataPointIndex, config }) => {
                            const selectedDay = this.chartData.find((item, index) => index === dataPointIndex)?.date;
                            this.selectedDay = selectedDay;

                            this.$nextTick(() => {
                                this.isTableView = true;
                                this.handleResized();
                            });
                        },
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    curve: 'straight',
                },
                fill: getApexChartsGradientFill(),
                grid: {
                    xaxis: {
                        lines: {
                            show: true,
                        },
                    },
                },
                tooltip: {
                    custom(data) {
                        const series = data.series && data.series[data.seriesIndex];
                        const seriesValue = series && series[data.dataPointIndex];
                        return `${'<div class="px-1 py-50"><span>'}${seriesValue || 0}</span></div>`;
                    },
                },
                xaxis: {
                    categories: this.chartData.map((departuresCount) => {
                        return format(parseISO(departuresCount.date), 'EEE dd');
                    }),
                },
                yaxis: {
                    tickAmount: 1,
                    labels: {
                        formatter: (val) => val.toFixed(),
                    },
                },
            };
        },
        getDataParams() {
            return {
                date: this.selectedDay,
            };
        },
        getDataEndpoint() {
            return DASHBOARD_DEPARTURES_GRID_ENDPOINT();
        },
    },
    mounted() {
        this.fetchChartData();
    },
    methods: {
        goBack() {
            this.isTableView = false;
        },
        async fetchChartData() {
            try {
                this.busy = true;
                const response = await API.get(DASHBOARD_DEPARTURES_CHART_DATA_ENDPOINT(), {
                    params: {
                        period_in_days: 14,
                        with_blocked: this.tabWidget.settings.display_blocked_reservations,
                    },
                });
                this.chartData = response.data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch chart data' });
            } finally {
                this.busy = false;
            }
        },
        handleResized() {
            this.limit = getWidgetGridRowsNumber({ tabWidget: this.tabWidget });
            this.cardGridKey += 1;
        },
    },
};
</script>
